/* eslint-disable no-console */
/**
 * Secure Logger Utility
 * Provides production-safe logging that prevents sensitive data leaks
 * to the browser console
 * 
 * NOTE: This file is allowed to use console.* methods directly as it's the
 * centralized logging wrapper. All other code MUST use secureLogger instead.
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

const NATIVE_CONSOLE = {
  debug: console.debug.bind(console),
  info: console.info.bind(console),
  warn: console.warn.bind(console),
  error: console.error.bind(console),
};

interface LoggerConfig {
  enableDebug: boolean;
  enableInfo: boolean;
  enableWarn: boolean;
  enableError: boolean;
  maskSensitiveData: boolean;
}

const SENSITIVE_PATTERNS = {
  // Authentication
  token: /token|jwt|bearer|auth|session|cookie/i,
  password: /password|passwd|pwd|pass/i,
  secret: /secret|apikey|api_key|key|credential/i,
  
  // Personal Information
  email: /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/,
  phone: /(\+?1)?[-.\s]?\(?[0-9]{3}\)?[-.\s]?[0-9]{3}[-.\s]?[0-9]{4}/,
  ssn: /\b\d{3}-\d{2}-\d{4}\b/,
  creditCard: /\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/,
  
  // System Information
  dbConnection: /mongodb|postgres|mysql|sqlite|connection|connectionstring/i,
  apiEndpoint: /endpoint|url|host|server|domain/i,
  environmentVar: /import\.meta\.env|VITE_|API_URL/i,
};

const SENSITIVE_KEYS = [
  'password', 'passwd', 'pwd', 'pass',
  'token', 'accessToken', 'access_token', 'refreshToken', 'refresh_token',
  'apiKey', 'api_key', 'secret', 'credential', 'credentials',
  'authorization', 'bearer',
  'ssn', 'socialSecurityNumber',
  'creditCard', 'cardNumber',
  'email', 'phone', 'telephone',
  'connectionString', 'dbUrl', 'DATABASE_URL',
  'privateKey', 'private_key', 'secretKey', 'secret_key'
];

class SecureLogger {
  private config: LoggerConfig;

  constructor(config: Partial<LoggerConfig> = {}) {
    const isProduction = import.meta.env.PROD;
    
    this.config = {
      enableDebug: !isProduction,
      enableInfo: !isProduction,
      enableWarn: true,
      enableError: true,
      maskSensitiveData: true,
      ...config
    };
  }

  /**
   * Mask sensitive values in strings
   */
  private maskString(value: string): string {
    if (!this.config.maskSensitiveData || !value || typeof value !== 'string') {
      return value;
    }

    // Mask emails
    if (SENSITIVE_PATTERNS.email.test(value)) {
      return value.replace(SENSITIVE_PATTERNS.email, '[EMAIL_REDACTED]');
    }

    // Mask credit cards
    if (SENSITIVE_PATTERNS.creditCard.test(value)) {
      return value.replace(SENSITIVE_PATTERNS.creditCard, '[CARD_REDACTED]');
    }

    // Mask SSNs
    if (SENSITIVE_PATTERNS.ssn.test(value)) {
      return value.replace(SENSITIVE_PATTERNS.ssn, '[SSN_REDACTED]');
    }

    // Mask phone numbers
    if (SENSITIVE_PATTERNS.phone.test(value)) {
      return value.replace(SENSITIVE_PATTERNS.phone, '[PHONE_REDACTED]');
    }

    // Mask tokens and secrets
    if (SENSITIVE_PATTERNS.secret.test(value) && value.length > 10) {
      return '[' + value.substring(0, 3) + '_REDACTED_' + value.substring(value.length - 3) + ']';
    }

    return value;
  }

  /**
   * Deep sanitize objects for logging
   */
  private sanitizeObject(obj: any, depth = 0): any {
    // Prevent infinite recursion
    if (depth > 10) {
      return '[MAX_DEPTH_REACHED]';
    }

    if (obj === null || obj === undefined) {
      return obj;
    }

    // Handle primitives
    if (typeof obj === 'string') {
      return this.maskString(obj);
    }

    if (typeof obj !== 'object') {
      return obj;
    }

    // Handle arrays
    if (Array.isArray(obj)) {
      return obj.map(item => this.sanitizeObject(item, depth + 1));
    }

    // Handle objects
    const sanitized: any = {};
    for (const [key, value] of Object.entries(obj)) {
      // Check if key name indicates sensitive data
      if (SENSITIVE_KEYS.some(sensitiveKey => key.toLowerCase().includes(sensitiveKey.toLowerCase()))) {
        sanitized[key] = '[REDACTED]';
        continue;
      }

      if (typeof value === 'string') {
        sanitized[key] = this.maskString(value);
      } else if (typeof value === 'object') {
        sanitized[key] = this.sanitizeObject(value, depth + 1);
      } else {
        sanitized[key] = value;
      }
    }

    return sanitized;
  }

  /**
   * Format log message with context
   */
  private formatMessage(level: LogLevel, message: string, ...args: any[]): [string, ...any[]] {
    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}] [${level.toUpperCase()}]`;
    
    const sanitizedArgs = args.map(arg => {
      if (typeof arg === 'object' && arg !== null) {
        return this.sanitizeObject(arg);
      }
      if (typeof arg === 'string') {
        return this.maskString(arg);
      }
      return arg;
    });

    return [prefix + ' ' + message, ...sanitizedArgs];
  }

  /**
   * Debug level logging (disabled in production)
   */
  debug(message: string, ...args: any[]): void {
    if (!this.config.enableDebug) return;
    const [formattedMessage, ...sanitized] = this.formatMessage('debug', message, ...args);
    NATIVE_CONSOLE.debug(formattedMessage, ...sanitized);
  }

  /**
   * Info level logging (disabled in production)
   */
  info(message: string, ...args: any[]): void {
    if (!this.config.enableInfo) return;
    const [formattedMessage, ...sanitized] = this.formatMessage('info', message, ...args);
    NATIVE_CONSOLE.info(formattedMessage, ...sanitized);
  }

  /**
   * Warning level logging (enabled in all environments)
   */
  warn(message: string, ...args: any[]): void {
    if (!this.config.enableWarn) return;
    const [formattedMessage, ...sanitized] = this.formatMessage('warn', message, ...args);
    NATIVE_CONSOLE.warn(formattedMessage, ...sanitized);
  }

  /**
   * Error level logging (enabled in all environments)
   */
  error(message: string, ...args: any[]): void {
    if (!this.config.enableError) return;
    const [formattedMessage, ...sanitized] = this.formatMessage('error', message, ...args);
    NATIVE_CONSOLE.error(formattedMessage, ...sanitized);
  }

  /**
   * Security event logging - always enabled
   */
  security(message: string, context?: any): void {
    const timestamp = new Date().toISOString();
    const sanitizedContext = context ? this.sanitizeObject(context) : {};
    NATIVE_CONSOLE.warn(`[${timestamp}] [SECURITY] ${message}`, sanitizedContext);
  }

  /**
   * Update logger configuration
   */
  configure(config: Partial<LoggerConfig>): void {
    this.config = { ...this.config, ...config };
  }
}

// Export singleton instance
export const secureLogger = new SecureLogger();
export default secureLogger;
